import { useNavigate, useLocation } from "react-router";
import BoardItem from "./BoardItem";
import BoardNoneItem from "./BoardNoneItem";
import SupportPanel from "./SupportPanel";
import { IOccurrence } from "../types";
import { ContentLoader, NotFoundState } from "@rio-cloud/rio-uikit";
import { useRef, useState } from "react";
import useDarkMode from "../../../hooks/useDarkMode";
import { useIntl } from "react-intl";

interface Props {
    data: IOccurrence[];
    isLoading: boolean;
}

const ListBoard = ({ data, isLoading }: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isDarkMode = useDarkMode();
    const intl = useIntl();

    const handleClickItem = (id: number | string) => {
        const params = new URLSearchParams(location.search);
        params.set('id', String(id));

        navigate(`${location.pathname}/details?${params.toString()}`);
    };

    const STATUS_ORDER = ["OPEN", "IN_ANALYSIS", "ANSWERED", "FINISHED"];

    const topScrollRef = useRef<HTMLDivElement>(null);
    const contentScrollRef = useRef<HTMLDivElement>(null);

    const handleTopScroll = () => {
        if (contentScrollRef?.current && topScrollRef?.current) {
            contentScrollRef.current.scrollLeft =
                topScrollRef.current.scrollLeft;
        }
    };

    const handleContentScroll = () => {
        if (contentScrollRef?.current && topScrollRef?.current) {
            topScrollRef.current.scrollLeft =
                contentScrollRef.current.scrollLeft;
        }
    };

    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    const startDrag = (e: React.MouseEvent) => {
        e.preventDefault();
        isDraggingRef.current = true;
        hasMovedRef.current = false;
        startXRef.current = e.pageX - (contentScrollRef.current?.offsetLeft || 0);
        scrollLeftRef.current = contentScrollRef.current?.scrollLeft || 0;
        setIsDragging(true);
    };

    const onDrag = (e: React.MouseEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        const x = e.pageX - (contentScrollRef.current?.offsetLeft || 0);
        const walk = (x - startXRef.current) * -1;
        if (Math.abs(walk) > 5) {
            hasMovedRef.current = true;
        }
        if (contentScrollRef.current) contentScrollRef.current.scrollLeft = scrollLeftRef.current + walk;
    };

    const stopDrag = (e: React.MouseEvent) => {
        isDraggingRef.current = false;
        setIsDragging(false);
        if (hasMovedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            const preventClicks = (event: Event) => {
                event.preventDefault();
                event.stopPropagation();
            };
            document.addEventListener('click', preventClicks, { capture: true });
            setTimeout(() => {
                document.removeEventListener('click', preventClicks, { capture: true });
            }, 100);
        }
    };

    const COLUMN_WIDTH = 350;
    const COLUMN_COUNT = 4;
    const GAP = 10;

    const BOARD_WIDTH =
        COLUMN_WIDTH * COLUMN_COUNT +
        GAP * (COLUMN_COUNT - 1);

    return (
        <div className="margin-top-15 bg-white dark:bg-neutral-800 padding-15 width-100pct">
            <div
                ref={topScrollRef}
                onScroll={handleTopScroll}
                style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    height: 20,
                    padding: 10,
                }}
            >
                <div style={{ width: BOARD_WIDTH, height: 1 }} />
            </div>
            <div
                className="width-100pct height-100pct display-flex flex-column overflow-x-scroll"
                style={{ background: isDarkMode ? '#171717' : '#D0DAE2', padding: 10, cursor: isDragging ? 'grabbing' : 'grab', userSelect: isDragging ? 'none' : 'auto' }}
                ref={contentScrollRef}
                onScroll={handleContentScroll}
                onMouseDown={startDrag}
                onMouseMove={onDrag}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
            >
                <div className="display-flex width-100pct" style={{ minWidth: BOARD_WIDTH }}>
                    <div className="width-25pct min-width-350">
                        <SupportPanel title={intl.formatMessage({ id: "intl-msg:tas.occurrence.card.open" })} />
                    </div>
                    <div className="width-25pct min-width-350">
                        <SupportPanel title={intl.formatMessage({ id: "intl-msg:tas.occurrence.card.inAnalysis" })} />
                    </div>
                    <div className="width-25pct min-width-350">
                        <SupportPanel title={intl.formatMessage({ id: "intl-msg:tas.occurrence.card.answered" })} />
                    </div>
                    <div className="width-25pct min-width-350">
                        <SupportPanel title={intl.formatMessage({ id: "intl-msg:tas.occurrence.card.finished" })} />
                    </div>
                </div>

                {isLoading ? Array.from(({ length: 2 })).map((_, l) => {
                    return (
                        <div key={l?.toString()} className="display-flex margin-bottom-10" style={{ minWidth: BOARD_WIDTH }}>
                            {Array.from({ length: STATUS_ORDER.length }).map((_, idx) => {
                                return (
                                    <div className="width-25pct min-width-350 height-200" key={idx?.toString()}>
                                        <ContentLoader className="width-100pct height-100pct border" />
                                    </div>
                                )
                            })}
                        </div>
                    )
                }) : null}

                {!isLoading && data?.length ? (
                    <div className="flex-column" style={{ minWidth: BOARD_WIDTH }}>
                        {data.map((item: IOccurrence, idx: number) => {
                            const order = STATUS_ORDER?.indexOf(
                                item.status
                            );

                            return (
                                <div className="display-flex margin-bottom-10" key={idx.toString()}>
                                    {Array.from({ length: STATUS_ORDER.length }).map((_, colIdx) => {
                                        const height = item.chassis ? 280: 250;
                                        return (
                                            <div className="width-25pct min-width-350" key={colIdx}>
                                                {colIdx === order ? (
                                                    <BoardItem onClick={() => handleClickItem(item?.id)} data={item} height={height} />
                                                ) : (
                                                    <BoardNoneItem height={height} />
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            );
                        })}
                    </div>
                ) : null}

                {!isLoading && !data?.length ? (
                    <div className="width-100pct margin-top-10">
                        <NotFoundState
                            headline={intl.formatMessage({ id: "intl-msg:support.listBoard.noOccurrenceHeadline" })}
                            message={intl.formatMessage({ id: "intl-msg:support.listBoard.noOccurrenceMessage" })}
                            outerClassName='border-none'
                        />
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default ListBoard;